<template>
  <b-container>
    <b-table
      responsive
      striped
      hover
      :fields="table_data.fields"
      :items="table_data.items"
      class="synaplus-cosmo-scroll-higher-720"
    />
  </b-container>
</template>

<i18n>
{
  "en": {
    "depth": "Depth",
    "nb_trees": "Number of trees",
    "per_level_observed_features": "Per tree level observed features (%)",
    "nb_gbdt_forests": "Number of GBDT forests",
    "per_gbdt_observed_samples": "Per GBDT observed samples (%)",
    "nb_trees_per_gbdt": "Number of trees per GBDT",
    "per_tree_nb_samples": "Per tree observed samples (%)",
    "regularization": "Regularisation",
    "polynom_degree": "Polynom degree",
    "bias": "Bias",
    "nb_bagged_svc": "Number of bagged SVC",
    "per_svc_nb_samples": "Per SVC observed samples (%)",
    "nb_iterations": "Number of iterations",
    "nb_factors": "Number of factors",
    "eta": "Eta",
    "learning_rate": "Learning rate",
    "nb_estimators": "Number of estimators",
    "nb_hidden_layers": "Number of hidden layers",
    "nb_total_neurons": "Total number of neurons",
    "exponential_factor": "Exponential factor",
    "dropout": "Dropout",
    "number_of_epochs": "Number of epochs",
    "iteration": "Iteration",
    "score": "Score",
    "model_name": "Model name",
    "hyperparameters": "Hyperparameters",
    "penalty_parameter": "Penaly parameter",
    "degree": "Degree",
    "coef0": "Intercept coefficient"
  },
  "fr": {
    "depth": "Profondeur",
    "nb_trees": "Nombre d'arbres",
    "per_level_observed_features": "Features observées par niveau d'arbre (%)",
    "nb_gbdt_forests": "Nombre de forêts de GBDTs",
    "per_gbdt_observed_samples": "Observations par forêt de GBDTs (%)",
    "nb_trees_per_gbdt": "Nombre d'arbres par GBDT",
    "per_tree_nb_samples": "Observations par arbre (%)",
    "regularization": "Régularisation",
    "polynom_degree": "Degré du polynome",
    "bias": "Coefficient 0",
    "nb_bagged_svc": "Nombre de SVC pour le bagging",
    "per_svc_nb_samples": "Observations par SVC (%)",
    "nb_iterations": "Nombre d'itérations",
    "nb_factors": "Nombre de facteurs",
    "eta": "Eta",
    "learning_rate": "Vitesse d'apprentissage",
    "nb_estimators": "Nombre d'estimateurs",
    "nb_hidden_layers": "Nombre de couches cachées",
    "nb_total_neurons": "Nombre total de neurones",
    "exponential_factor": "Facteur exponentiel",
    "dropout": "Dropout",
    "number_of_epochs": "Nombre d'époques",
    "iteration": "Itération",
    "score": "Score",
    "model_name": "Nom du modèle",
    "hyperparameters": "Hyperparamètres",
    "penalty_parameter": "Paramètre de pénalité",
    "degree": "Degré",
    "coef0": "Coefficient d'interception"
  }
}
</i18n>

<script>
import _ from 'lodash'

import { round_value } from '@/utils.js'

import { subscription_mixin } from '@/components/mixin.js'

// *****************************************************************************

export function is_valid(elt) {
  let res = !_.isUndefined(elt) && !_.isNull(elt)

  if (_.isArray(elt)) {
    res =
      res && (_.isEmpty(elt) || _.reduce(elt, (x, n) => x && is_valid(n), true))
  } else {
    res = res && !_.isNaN(elt)
  }
  return res
}

// *****************************************************************************

export default {
  name: 'PerformancesTableCaruana',
  components: {},
  mixins: [subscription_mixin],
  props: {
    project_id: {
      required: true,
    },
    is_caruana: {
      required: true,
    },
    is_cv: {
      required: true,
    },
    is_single_cv: {
      required: true,
    },
    tracking_infos: {
      required: true,
    },
    model_name: {
      required: false,
      default: '',
    },
    models: {
      required: false,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      hps_names: {
        small_gbdt: [
          this.$t('depth'),
          this.$t('nb_trees'),
          this.$t('per_level_observed_features'),
        ],
        gbdt: [
          this.$t('depth'),
          this.$t('nb_trees'),
          this.$t('per_level_observed_features'),
        ],
        gbdt_forest: [
          this.$t('nb_gbdt_forests'),
          this.$t('per_gbdt_observed_samples'),
          this.$t('depth'),
          this.$t('nb_trees_per_gbdt'),
          this.$t('per_level_observed_features'),
        ],
        random_forest: [
          this.$t('per_tree_nb_samples'),
          this.$t('nb_trees'),
          this.$t('depth'),
          this.$t('per_level_observed_features'),
        ],
        poly_svc: [
          this.$t('regularization'),
          this.$t('polynom_degree'),
          this.$t('bias'),
        ],
        rbf_svc: [this.$t('regularization')],
        rbf_svc_forest: [
          this.$t('nb_bagged_svc'),
          this.$t('per_svc_nb_samples'),
          this.$t('regularization'),
        ],
        logistic_regression: [
          this.$t('regularization'),
          this.$t('nb_iterations'),
        ],
        fm: [
          this.$t('nb_factors'),
          this.$t('eta'),
          this.$t('regularization'),
          this.$t('nb_iterations'),
        ],
        xgboost: [
          this.$t('learning_rate'),
          this.$t('nb_estimators'),
          this.$t('depth'),
          this.$t('per_level_observed_features'),
          this.$t('regularization'),
        ],
        lightgbm: [
          this.$t('learning_rate'),
          this.$t('nb_estimators'),
          this.$t('depth'),
          this.$t('per_level_observed_features'),
          this.$t('regularization'),
        ],
        catboost: [
          this.$t('learning_rate'),
          this.$t('nb_estimators'),
          this.$t('depth'),
          this.$t('per_level_observed_features'),
          this.$t('regularization'),
        ],
        pytorch_mlp: [
          this.$t('nb_hidden_layers'),
          this.$t('nb_total_neurons'),
          this.$t('exponential_factor'),
          this.$t('dropout'),
          this.$t('number_of_epochs'),
        ],
        naive_bayes_gaussian: [],
        naive_bayes_bernoulli: [],
      },
    }
  },
  computed: {
    score_fields() {
      let res = [
        {
          key: 'iter_nb',
          label: this.$t('iteration'),
          sortable: true,
          class: 'text-center',
          variant: 'secondary',
        },
      ]

      _.forEach(this.hps_names[this.model_name], x =>
        res.push({
          key: x,
          label: x,
          sortable: false,
          class: 'text-center',
        })
      )

      res.push({
        key: 'score',
        label: this.$t('score'),
        sortable: true,
        class: 'text-center',
        variant: 'primary',
      })

      return res
    },
    caruana_score_fields() {
      let res = [
        {
          key: 'iteration',
          label: this.$t('iteration'),
          sortable: true,
          class: 'text-center',
          variant: 'secondary',
        },
        {
          key: 'score',
          label: this.$t('score'),
          sortable: true,
          class: 'text-center',
          variant: 'primary',
        },
        {
          key: 'model_name',
          label: this.$t('model_name'),
          sortable: false,
          class: 'text-center',
        },
        {
          key: 'hps',
          label: this.$t('hyperparameters'),
          sortable: false,
          class: 'text-center',
        },
      ]
      return res
    },
    caruana_iterations_fields() {
      let fields = [
        {
          key: 'iteration',
          label: this.$t('iteration'),
          sortable: true,
          class: 'text-center',
          variant: 'secondary',
        },
      ]

      _.forEach((this.tracking_infos || {}).model_searches, (value, key) =>
        fields.push({
          key: key,
          label: key,
          sortable: false,
          class: 'text-center',
        })
      )

      fields.push({
        key: 'score',
        label: this.$t('score'),
        sortable: true,
        class: 'text-center',
        variant: 'primary',
      })

      return fields
    },
    single_cv_data() {
      let entries = []
      let fields = []
      let data = (
        this.fetch_poid_tracking_info('single_cv', this.tracking_infos) || {}
      ).iterations

      let names = this.hps_names[this.model_name]
      _.forEach(
        _.filter(data, function(x) {
          return (
            is_valid(x.value) &&
            _.reduce(
              _.zip(names, ((x || {}).hyperparameters || [])[1]),
              function(res, n) {
                return res && is_valid(n[1])
              }
            )
          )
        }),
        function(value, key) {
          let o = {
            iter_nb: parseInt(key),
            score: -round_value(value.value),
          }
          _.forEach(
            _.zip(names, ((value || {}).hyperparameters || [])[1]),
            elt => (o[elt[0]] = round_value(elt[1]))
          )
          entries.push(o)
        }
      )
      return entries
    },
    full_cv_data() {
      let entries = []
      let fields = []
      let data = (
        this.fetch_poid_tracking_info('full_cv', this.tracking_infos) || {}
      ).iterations

      let names = this.hps_names[this.model_name]
      _.forEach(
        _.filter(data, function(x) {
          return (
            is_valid(x.values) &&
            _.reduce(_.zip(names, (x || {}).hyperparameters), function(res, n) {
              return res && is_valid(n)
            })
          )
        }),
        function(value, key) {
          let o = {
            iter_nb: parseInt(key),
            score: -round_value(_.mean((value || {}).values)),
          }
          _.forEach(
            _.zip(names, (value || {}).hyperparameters),
            elt => (o[elt[0]] = round_value(elt[1]))
          )
          entries.push(o)
        }
      )
      return entries
    },
    caruana_single_cv_data() {
      let entries = []
      let data = this.tracking_infos

      let iter_count = 0
      // console.log(
      //   'caruana_single_cv_data',
      //   JSON.stringify(this.models),
      //   JSON.stringify(data)
      // )

      _.forEach(this.models, function(model_name) {
        entries = _.concat(
          entries,
          _.map(
            _.filter((data[model_name] || {}).iterations, function(x) {
              return (
                is_valid(x) && is_valid(x.hyperparameters) && is_valid(x.value)
              )
            }),
            function(elt) {
              ++iter_count
              return {
                iteration: iter_count - 1,
                model_name: model_name,
                hps:
                  '[' +
                  _.map(((elt || {}).hyperparameters || [])[1], x =>
                    round_value(x)
                  ) +
                  ']',
                score: round_value(-(elt || {}).value || {}),
              }
            }
          )
        )
      })

      return entries
    },
    caruana_full_cv_data() {
      let entries = []
      let data = this.tracking_infos

      let iter_count = 0

      _.forEach(this.models, function(model_name) {
        entries = _.concat(
          entries,
          _.map(
            _.filter((data[model_name] || {}).iterations, function(x) {
              return (
                is_valid(x) && is_valid(x.hyperparameters) && is_valid(x.values)
              )
            }),
            function(elt) {
              ++iter_count
              return {
                iteration: iter_count - 1,
                model_name: model_name,
                hps:
                  '[' +
                  _.map((elt || {}).hyperparameters, x => round_value(x)) +
                  ']',
                score: round_value(-_.mean((elt || {}).values || {})),
              }
            }
          )
        )
      })
      return entries
    },
    caruana_iterations_data() {
      let entries = []
      let models_counters = {}
      let data = (this.tracking_infos || {}).caruana_iterations

      let best_score = Number.NEGATIVE_INFINITY
      let best_iter_nb = -1

      _.forEach(data, x => (models_counters[x.model_name] = 0))

      _.forEach(
        _.filter(data, function(elt) {
          return is_valid(elt.value)
        }),
        function(value, key) {
          let model = value.model_name
          models_counters[model] += 1
          let variant = {}
          variant[model] = 'warning'
          entries.push(
            _.merge(
              {
                iteration: parseInt(key, 10),
                score: round_value(-(value || {}).value),
                _cellVariants: variant,
              },
              models_counters
            )
          )

          let current_score = Number.parseFloat(-(value || {}).value)
          if (current_score > best_score) {
            best_score = current_score
            best_iter_nb = parseInt(key, 10)
          }
        }
      )

      if (best_iter_nb !== -1) {
        entries[best_iter_nb]['_rowVariant'] = 'success'
      }

      return entries
    },
    table_data() {
      if (this.is_caruana) {
        if (this.is_cv) {
          if (this.is_single_cv) {
            return {
              fields: this.caruana_score_fields,
              items: this.caruana_single_cv_data,
            }
          } else {
            return {
              fields: this.caruana_score_fields,
              items: this.caruana_full_cv_data,
            }
          }
        } else {
          return {
            fields: this.caruana_iterations_fields,
            items: this.caruana_iterations_data,
          }
        }
      } else {
        if (this.is_single_cv) {
          return {
            fields: this.score_fields,
            items: this.single_cv_data,
          }
        } else {
          return {
            fields: this.score_fields,
            items: this.full_cv_data,
          }
        }
      }
    },
  },
}
</script>
